import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, get, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    expertImg = "expertImg",
}

export interface CenHomePageExpertiseStyles {
    contentContainer?: InjectableCss;
    titleStyles?: TypographyProps;
    descriptionStyles?: InjectableCss;
    linkStyles?: InjectableCss;
    imgWrapper?: InjectableCss;
    imgStyles?: InjectableCss;
    mainContainer?: InjectableCss;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.expertImg]: string;
    };
}

export const cenHomePageExpertiseStyles: CenHomePageExpertiseStyles = {
    mainContainer: {
        css: css`
            display: flex;
            width: 100%;
            gap: 24px;
            background-color: ${COLORS.background.grayShade};
            padding: 104px 64px 104px 64px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 16px;
                        background-color: ${get(theme, "colors.info.contrast")};
                    `,
                    css`
                        padding: 32px 16px;
                        background-color: ${get(theme, "colors.info.contrast")};
                    `,
                    css`
                        padding: 40px 16px 40px 0px;
                    `,
                    css``,
                ])}
        `,
    },
    contentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 64px;
            width: 50%;
            background-color: ${getColor("info.contrast")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding: 0px;
                        background: none;
                    `,
                    css`
                        width: 100%;
                        background: none;
                        padding: 0px;
                    `,
                    css`
                        padding: 32px;
                    `,
                    css``,
                ])}
        `,
    },
    titleStyles: {
        variant: "h3",
        css: css`
            font-family: Poppins;
            font-size: 32px !important;
            font-weight: 700 !important;
            line-height: 40px !important;
            text-align: left;
            margin-bottom: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.largest} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                        line-height: 32px !important;
                        width: 100%;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.largest} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                        line-height: 32px !important;
                        width: 100%;
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.largest} !important;
                        font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                        line-height: 32px !important;
                        text-align: left;
                    `,
                    css``,
                ])}
        `,
    },
    linkStyles: {
        css: css`
            span {
                font-family: Poppins;
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                text-align: left;
            }
        `,
    },
    imgWrapper: {
        css: css`
            width: 50%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
};
const styles = cenHomePageExpertiseStyles;

const CENHomePageExpertise: FC<OwnProps> = ({ fields }) => {
    const [status, setStatus] = useState(false);
    const toggleStatus = () => {
        setStatus(!status);
    };

    const applyLineClamp = status
        ? `
                webkit-box-orient: vertical;
                -webkit-line-clamp: unset;
                overflow: initial;
                -webkit-box-orient:unset;
                display:block;
                text-overflow: unset;
    `
        : `
                  display: -webkit-box;
                 -webkit-box-orient: vertical;
                 -webkit-line-clamp: 6;
                  overflow: hidden;
                  width:100%;
                  text-overflow: ellipsis;

    `;
    const descriptionStyles = {
        css: css`
            .mainContainerExpertise {
                ${applyLineClamp}
            }
            p {
                font-family: Poppins;
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 28px;
                text-align: left;
                color: ${getProp("theme.typography.p.color")};
            }
        `,
    };

    // const sample = status ? `height:100%;` : `height:auto;`;
    const imgStyles = {
        css: css`
            width: 100%;
            height: auto;
        `,
    };
    return (
        <StyledWrapper {...styles.mainContainer}>
            <StyledWrapper {...styles.imgWrapper}>
                <Img src={fields.expertImg} altText={fields.title} {...imgStyles} />
            </StyledWrapper>
            <StyledWrapper {...styles.contentContainer}>
                <Typography as="h3" {...styles.titleStyles}>
                    {fields.title}
                </Typography>
                <StyledWrapper {...descriptionStyles}>
                    {parse(transformContentHtml(fields.description), parserOptions)}
                </StyledWrapper>
                <Link href="" onClick={toggleStatus} {...styles.linkStyles}>
                    {status ? "Show Less" : "Show More"}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENHomePageExpertise,
    definition: {
        group: "Common",
        displayName: "CEN HomePage Expertise",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.description,
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.expertImg,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
